import { PlusIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { OutlineNode } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { useWorkbookProcess } from './useWorkbookProcess';
import { useExecutionLabel } from './useExecutionLabel';

/** Adds an Instruction after `after` (under a root: first under it; after a Data note: another on its Data item), or at the end. */
export function AddInstructionButton({ after }: { after?: OutlineNode }) {
  const gt = useGT();
  const { outline, references, canUpdate, generation, insert } =
    useWorkbookProcess();
  const { label } = useExecutionLabel(references);
  if (!canUpdate || outline.full) return null;
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={generation.generating}
      className="h-7 justify-start"
      onClick={() => {
        if (after?.kind !== 'instruction') return insert(after?.id ?? null);
        // After a Data note, the new step is a Data note on the same Data item.
        const dataItem = outline.dataItemOf(after.id);
        insert(after.parentId, after.id, dataItem ? [dataItem] : undefined);
      }}
      aria-label={
        after
          ? gt('Add instruction after {name}', {
              context: 'workbook insert instruction',
              name: label(after),
            })
          : gt('Add instruction', {
              context: 'workbook add process instruction',
            })
      }
    >
      <PlusIcon />
      <T context="workbook add process instruction">Add instruction</T>
    </Button>
  );
}

/**
 * A Process header's actions, kept out of the way while reading: hidden until
 * the pointer is over its `group/head` row or focus is inside it, and while a
 * menu it opened is open. Where nothing hovers (touch) they always show.
 */
export const HEAD_ACTIONS =
  'flex items-center gap-1 transition-opacity motion-reduce:transition-none [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/head:opacity-100 [@media(hover:hover)]:group-focus-within/head:opacity-100 [@media(hover:hover)]:group-has-[[aria-expanded=true]]/head:opacity-100';

/**
 * An Instruction's actions trigger: an icon in the step's own right gutter
 * (`STEP_GUTTER`), so it never covers the text or a Reference chip in it.
 * Hidden until the pointer is over its `group/step` or focus is inside, and
 * while its menu is open; where nothing hovers (touch) it always shows.
 */
export const STEP_ACTIONS_TRIGGER =
  'absolute right-0 top-0 size-7 transition-opacity motion-reduce:transition-none [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/step:opacity-100 [@media(hover:hover)]:group-focus-within/step:opacity-100 [@media(hover:hover)]:aria-expanded:opacity-100';

/** The right gutter a step keeps for its actions trigger. */
export const STEP_GUTTER = 'pr-8';
